import type { QueryRunFilter, QueryRunVerdict, RunFilter, RunVerdict } from '@console/types'

/**
 * The one place the contract's filter vocabulary meets the console's own.
 *
 * These are genuinely different shapes, not a naming inconsistency to tidy
 * away (see `types/src/query.ts`):
 *
 *   contract              console (Runs page URL state)
 *   ─────────────────     ────────────────────────────
 *   verdict: string[]     verdict?: single
 *   doesnt_match          mismatch
 *   not_confirmed_yet     (no equivalent)
 *   action_type: string[] actionType?: single
 *   line_item_text        lineItemContains
 *   (none)                amountAbove
 *
 * A `filter` result routes to the Runs page (spec §5.1), so it has to cross
 * this boundary. Where the contract can express something the Runs page
 * cannot — two verdicts at once, or a verdict the console has no word for —
 * `runsUrlWouldLoseMeaning` says so, and the caller renders the result inline
 * instead of navigating. Silently dropping half a filter would show the
 * operator a list that doesn't answer what they asked.
 */

const CONTRACT_TO_CONSOLE_VERDICT: Record<QueryRunVerdict, RunVerdict | null> = {
  confirmed: 'confirmed',
  refused: 'refused',
  needs_you: 'needs_you',
  doesnt_match: 'mismatch',
  not_confirmed_yet: null,
}

/** True when navigating to `/runs` would answer a narrower question than the one asked. */
export function runsUrlWouldLoseMeaning(filter: QueryRunFilter): boolean {
  if (filter.verdict !== undefined) {
    if (filter.verdict.length > 1) return true
    const only = filter.verdict[0]
    if (only !== undefined && CONTRACT_TO_CONSOLE_VERDICT[only] === null) return true
  }
  if (filter.action_type !== undefined && filter.action_type.length > 1) return true
  // `rule_fired` has no Runs-page control at all.
  if (filter.rule_fired !== undefined) return true
  return false
}

/**
 * Only call when `runsUrlWouldLoseMeaning` is false — otherwise this narrows
 * the filter by construction.
 */
export function toConsoleRunFilter(filter: QueryRunFilter): RunFilter {
  const verdictCode = filter.verdict?.[0]
  const verdict = verdictCode === undefined ? undefined : CONTRACT_TO_CONSOLE_VERDICT[verdictCode]

  return {
    ...(verdict === null || verdict === undefined ? {} : { verdict }),
    ...(filter.date_from === undefined ? {} : { dateFrom: filter.date_from }),
    ...(filter.date_to === undefined ? {} : { dateTo: filter.date_to }),
    ...(filter.customer === undefined ? {} : { customer: filter.customer }),
    ...(filter.action_type?.[0] === undefined ? {} : { actionType: filter.action_type[0] }),
    ...(filter.line_item_text === undefined ? {} : { lineItemContains: filter.line_item_text }),
  }
}

/** Human-readable description of a contract filter, built from its fields — never from model prose. */
export function describeFilter(filter: QueryRunFilter): string {
  const parts: string[] = []
  if (filter.verdict !== undefined)
    parts.push(filter.verdict.map((v) => v.replace(/_/g, ' ')).join(' or '))
  if (filter.customer !== undefined) parts.push(`customer matching "${filter.customer}"`)
  if (filter.action_type !== undefined) parts.push(filter.action_type.join(' or '))
  if (filter.rule_fired !== undefined) parts.push(`rule ${filter.rule_fired}`)
  if (filter.line_item_text !== undefined)
    parts.push(`line items matching "${filter.line_item_text}"`)
  if (filter.date_from !== undefined) {
    parts.push(`from ${new Date(filter.date_from).toLocaleDateString()}`)
  }
  if (filter.date_to !== undefined)
    parts.push(`to ${new Date(filter.date_to).toLocaleDateString()}`)
  return parts.length === 0 ? 'all runs' : parts.join(' · ')
}
